/* De Vergulde Kaars: collection and search helpers.
   1. Colour (tag) filter links keep the current filters and sort order.
   2. A floating "Filteren en sorteren" button on phones once the in-page button scrolls away
      (hidden while scrolling down).
   3. Close button in the mobile filter drawer.
   4. Opening the mobile filter drawer moves focus into it (the trigger sits under the overlay).
   5. Picking a colour in the mobile drawer reopens the drawer after the page load.
   6. Desktop filter dropdowns close once their trigger scrolls out of view. */
(() => {
  if (window.dvkCollectionInit) return;
  window.dvkCollectionInit = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-dvk-keep-query]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    const query = params.toString();
    if (!query) return;
    event.preventDefault();
    const url = new URL(link.href, window.location.origin);
    window.location.href = `${url.pathname}?${query}`;
  });

  const initFloatingFilter = () => {
    const summary = document.querySelector('.mobile-facets__wrapper .mobile-facets__open-wrapper');
    const grid = document.getElementById('ProductGridContainer');
    if (!summary || !grid || document.querySelector('.dvk-filter-fab')) return;

    const label = summary.querySelector('.mobile-facets__open-label');
    const fab = document.createElement('button');
    fab.type = 'button';
    fab.className = 'dvk-filter-fab';
    fab.setAttribute('aria-hidden', 'true');
    fab.tabIndex = -1;
    fab.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/></svg>' +
      `<span>${label ? label.textContent.trim() : ''}</span>`;
    fab.addEventListener('click', () => {
      const current = document.querySelector('.mobile-facets__wrapper .mobile-facets__open-wrapper');
      if (current) current.click();
    });
    document.body.appendChild(fab);

    let summaryAbove = false;
    let gridVisible = false;
    let gridEndAbove = false;
    // Out of the way while the shopper scrolls down through the cards (it would sit on the
    // quick add buttons); back as soon as they scroll up, the moment they look for it.
    let scrollingDown = false;
    let lastY = window.scrollY;
    const update = () => {
      const show = summaryAbove && gridVisible && !gridEndAbove && !scrollingDown;
      fab.classList.toggle('is-visible', show);
    };

    // Hide once the end of the product list rises above the button zone, so the pill
    // never sits on top of the pagination or the footer.
    let ticking = false;
    const checkEnd = () => {
      ticking = false;
      const list = document.getElementById('product-grid') || grid;
      const bottom = list.getBoundingClientRect().bottom;
      const next = bottom < window.innerHeight - 96;
      if (next !== gridEndAbove) {
        gridEndAbove = next;
        update();
      }
    };
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) > 8) {
        const down = y > lastY;
        lastY = y;
        if (down !== scrollingDown) {
          scrollingDown = down;
          update();
        }
      }
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(checkEnd);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    checkEnd();

    new IntersectionObserver(([entry]) => {
      summaryAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    }).observe(summary);

    new IntersectionObserver(
      ([entry]) => {
        gridVisible = entry.isIntersecting;
        update();
      },
      { rootMargin: '0px 0px -30% 0px' },
    ).observe(grid);
  };

  // The mobile filter drawer's close button sits in the drawer header (not in the summary).
  // Capture phase, so Dawn's MenuDrawer "submenu back button" handler does not also run.
  document.addEventListener(
    'click',
    (event) => {
      const close = event.target.closest?.('.mobile-facets__close');
      if (!close) return;
      const drawer = close.closest('menu-drawer');
      const summary = drawer?.querySelector('details > summary');
      if (!drawer || typeof drawer.closeMenuDrawer !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      drawer.closeMenuDrawer(event, summary);
      summary?.setAttribute('aria-expanded', 'false');
      summary?.focus();
    },
    true,
  );

  // 4. Focus into the drawer on open, and keep the hidden trigger out of the Tab cycle.
  const focusDrawer = (summary) => {
    const details = summary.closest('details');
    window.setTimeout(() => {
      if (!details?.hasAttribute('open')) return;
      const panel = details.querySelector('.mobile-facets__inner');
      const close = details.querySelector('.mobile-facets__close');
      if (panel && close && typeof window.trapFocus === 'function') window.trapFocus(panel, close);
      else close?.focus();
    }, 0);
  };
  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('.mobile-facets__wrapper summary.mobile-facets__open-wrapper');
    if (summary) focusDrawer(summary);
  });

  // 5. Colour options are links (a page load); remember to reopen the drawer on arrival.
  const REOPEN_KEY = 'dvkReopenFilters';
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('.mobile-facets__wrapper a.dvk-color-list__link');
      if (!link) return;
      try {
        window.sessionStorage.setItem(REOPEN_KEY, new URL(link.href, window.location.origin).pathname);
      } catch (e) {}
    },
    true,
  );
  const reopenDrawer = () => {
    let target = null;
    try {
      target = window.sessionStorage.getItem(REOPEN_KEY);
      window.sessionStorage.removeItem(REOPEN_KEY);
    } catch (e) {}
    if (!target || target !== window.location.pathname) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const summary = document.querySelector('.mobile-facets__wrapper summary.mobile-facets__open-wrapper');
    if (summary && !summary.closest('details').hasAttribute('open')) summary.click();
  };

  // 6. A desktop dropdown whose trigger went under the sticky header or off screen closes,
  // so it never floats over the grid without a visible way to close it.
  let facetTicking = false;
  const closeOrphanedFacets = () => {
    facetTicking = false;
    const open = document.querySelectorAll('details.facets__disclosure.disclosure-has-popup[open]');
    if (!open.length) return;
    const header = document.querySelector('.shopify-section-header-sticky');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    open.forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;
      const rect = summary.getBoundingClientRect();
      if (rect.top < headerBottom || rect.bottom > window.innerHeight) {
        const hadFocus = details.contains(document.activeElement);
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
        if (hadFocus) summary.focus({ preventScroll: true });
      }
    });
  };
  window.addEventListener(
    'scroll',
    () => {
      if (facetTicking) return;
      facetTicking = true;
      window.requestAnimationFrame(closeOrphanedFacets);
    },
    { passive: true },
  );

  const init = () => {
    initFloatingFilter();
    reopenDrawer();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
